import { useEffect, useRef, useState } from 'react';
import {
  type AttackEvent,
  type Stats,
  recentResponseSchema,
  serverMessageSchema,
  statsSchema,
} from '@repo/shared';

/** Newest-first, capped so a long-running tab stays light. */
const MAX_EVENTS = 200;

/** Keepalive cadence; kept sparse because every client message wakes the DO. */
const PING_INTERVAL_MS = 30_000;
/**
 * A healthy socket receives stats every ~2s and a pong for every ping, so this
 * much silence means the connection is dead even if readyState still says OPEN
 * (laptop sleep, network change). Force-close so the reconnect logic kicks in;
 * the server replays a fresh snapshot on every connect, which backfills.
 */
const STALE_SOCKET_MS = 45_000;
const WATCHDOG_INTERVAL_MS = 15_000;

export type ConnectionStatus = 'connecting' | 'live' | 'reconnecting' | 'offline';

export interface EventStream {
  events: AttackEvent[];
  /** Never null — falls back to zeroed stats until the first message arrives. */
  stats: Stats;
  status: ConnectionStatus;
  /** The most recent event, handy for one-off animations. */
  latest: AttackEvent | null;
}

const EMPTY_STATS: Stats = {
  totalAttacks: 0,
  totalRequests: 0,
  attacksPerMin: 0,
  firstSeen: null,
  topUsernames: [],
  topPasswords: [],
  topPaths: [],
  topCountries: [],
  topClassifications: [],
  topCredentials: [],
};

/**
 * Last-known stats survive a refresh so the header doesn't flash to zeros
 * while the snapshot fetch is in flight. Session-scoped: a fresh visit still
 * starts clean, and stale numbers only ever live for one round trip.
 */
const STATS_CACHE_KEY = 'honeypot-theater:last-stats';

function readCachedStats(): Stats | null {
  try {
    const raw = sessionStorage.getItem(STATS_CACHE_KEY);
    if (!raw) return null;
    const parsed = statsSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

function writeCachedStats(stats: Stats): void {
  try {
    sessionStorage.setItem(STATS_CACHE_KEY, JSON.stringify(stats));
  } catch {
    /* storage may be full or blocked; the cache is best-effort */
  }
}

/** Exported for tests. */
export function mergeNewest(prev: AttackEvent[], incoming: AttackEvent[]): AttackEvent[] {
  // Incoming is newest-first; drop ids we already have, then cap.
  const seen = new Set(prev.map((e) => e.id));
  const fresh = incoming.filter((e) => !seen.has(e.id));
  return [...fresh, ...prev].slice(0, MAX_EVENTS);
}

/**
 * Connects to the EventHub WebSocket and exposes a live, capped event list plus
 * rolling stats. Seeds instantly from `/api/recent` so a lone visitor never
 * sees an empty screen, then streams. Reconnects with capped backoff and pauses
 * cleanly when the tab is hidden.
 */
export function useEventStream(): EventStream {
  const [events, setEvents] = useState<AttackEvent[]>([]);
  const [stats, setStats] = useState<Stats | null>(readCachedStats);
  const [status, setStatus] = useState<ConnectionStatus>('connecting');

  const socketRef = useRef<WebSocket | null>(null);
  const backoffRef = useRef(500);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const watchdogTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastMessageRef = useRef(0);
  const closedRef = useRef(false);
  // True once stats arrive over the socket; a cached or fetched seed must not
  // overwrite live data, only the other way around.
  const liveStatsRef = useRef(false);

  useEffect(() => {
    if (stats) writeCachedStats(stats);
  }, [stats]);

  useEffect(() => {
    closedRef.current = false;

    // Instant first paint: one-shot snapshot fetch, independent of the socket.
    void fetch('/api/recent')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data) => {
        const parsed = recentResponseSchema.safeParse(data);
        if (!parsed.success || closedRef.current) return;
        setEvents((prev) => (prev.length ? prev : [...parsed.data.events].reverse()));
        // The snapshot fetch is fresher than a cached seed but staler than the
        // socket; only apply it if live stats haven't landed yet.
        setStats((prev) => (liveStatsRef.current ? prev : parsed.data.stats));
      })
      .catch(() => {
        /* the socket is the real source of truth; ignore */
      });

    const connect = () => {
      if (closedRef.current) return;
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const ws = new WebSocket(`${proto}://${window.location.host}/api/ws`);
      socketRef.current = ws;

      // Per-socket timers: a superseded socket closing late must only ever
      // clear its own, never the current connection's.
      let pingTimer: ReturnType<typeof setInterval> | null = null;
      let watchdogTimer: ReturnType<typeof setInterval> | null = null;

      ws.onopen = () => {
        backoffRef.current = 500;
        lastMessageRef.current = Date.now();
        setStatus('live');
        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, PING_INTERVAL_MS);
        watchdogTimer = setInterval(() => {
          if (Date.now() - lastMessageRef.current > STALE_SOCKET_MS) ws.close();
        }, WATCHDOG_INTERVAL_MS);
        pingTimerRef.current = pingTimer;
        watchdogTimerRef.current = watchdogTimer;
      };

      ws.onmessage = (ev) => {
        lastMessageRef.current = Date.now();
        if (typeof ev.data !== 'string' || ev.data === 'pong') return;
        let payload: unknown;
        try {
          payload = JSON.parse(ev.data);
        } catch {
          return;
        }
        const msg = serverMessageSchema.safeParse(payload);
        if (!msg.success) return;
        const data = msg.data;

        if (data.type === 'snapshot') {
          const snapshot = [...data.events].reverse();
          setEvents((prev) => mergeNewest(prev, snapshot));
          liveStatsRef.current = true;
          setStats(data.stats);
        } else if (data.type === 'event') {
          const event = data.event;
          setEvents((prev) => mergeNewest(prev, [event]));
        } else {
          liveStatsRef.current = true;
          setStats(data.stats);
        }
      };

      const scheduleReconnect = () => {
        if (closedRef.current) return;
        setStatus('reconnecting');
        const delay = backoffRef.current;
        backoffRef.current = Math.min(delay * 2, 15_000);
        retryTimerRef.current = setTimeout(connect, delay);
      };

      ws.onclose = () => {
        if (pingTimer) clearInterval(pingTimer);
        if (watchdogTimer) clearInterval(watchdogTimer);
        // A superseded socket closing late must not spawn a second connection.
        if (socketRef.current !== ws) return;
        scheduleReconnect();
      };
      ws.onerror = () => ws.close();
    };

    connect();

    // Reconnect promptly when returning to a previously-backgrounded tab.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const ws = socketRef.current;
      if (!ws || ws.readyState === WebSocket.CLOSED) {
        backoffRef.current = 500;
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        connect();
      } else if (
        ws.readyState === WebSocket.OPEN &&
        Date.now() - lastMessageRef.current > STALE_SOCKET_MS
      ) {
        // Zombie from a sleep/network change: readyState lies, the silence
        // doesn't. Closing hands recovery to the reconnect logic.
        backoffRef.current = 500;
        ws.close();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      closedRef.current = true;
      setStatus('offline');
      document.removeEventListener('visibilitychange', onVisible);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      if (watchdogTimerRef.current) clearInterval(watchdogTimerRef.current);
      socketRef.current?.close();
    };
  }, []);

  return { events, stats: stats ?? EMPTY_STATS, status, latest: events[0] ?? null };
}
